import React, {useCallback, useEffect, useState} from 'react';
import {Button, Card, Form, Select, Space, Table, Tag} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined} from '@/icons';
import {useAppStore} from '@/page/app/context';
import AppContent from '@/page/component/AppLayout/AppContent';
import {deleteHrTemplate, getHrTemplates, toggleHrTemplate} from '@/api/hrTemplate';
import TemplateFormModal from './TemplateFormModal';
import {getVisibleSpecificTypes, ROUTINE_TYPES, SPECIFIC_TYPES} from '../routineUtils';
import {formatApiTime} from '../utils';

const TYPES = {...ROUTINE_TYPES, ...SPECIFIC_TYPES};
// 下拉可选项按“当前开放的类型”生成；TYPES 仍保留全量用于历史数据的中文名回显
const TYPE_OPTIONS = Object.entries({
    ...ROUTINE_TYPES,
    ...Object.fromEntries(getVisibleSpecificTypes().map(type => [type, SPECIFIC_TYPES[type]])),
}).map(([value, label]) => ({value, label}));

export default function TemplatePage() {
    const [{appId}] = useAppStore();
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const {data} = await getHrTemplates(appId, form.getFieldsValue());
            setRows(data || []);
        } finally {
            setLoading(false);
        }
    }, [appId, form]);

    useEffect(() => {
        if (appId) load();
    }, [appId, load]);

    const columns = [
        {title: '模板名称', dataIndex: 'templateName'},
        {title: '通知类别', dataIndex: 'taskType', render: value => TYPES[value] || value},
        {
            title: '变体',
            dataIndex: 'vacationType',
            render: value =>
                value === 'MATERNITY' ? '妈妈版' : value === 'PATERNITY' ? '爸爸版' : '-',
        },
        {
            title: '状态',
            dataIndex: 'enabled',
            width: 90,
            render: value => <Tag color={value ? 'green' : 'blue'}>{value ? '启用' : '停用'}</Tag>,
        },
        {
            title: '修改时间',
            dataIndex: 'modifyTime',
            width: 170,
            render: formatApiTime,
        },
        {
            title: '操作',
            width: 140,
            render: (_, row) => (
                <Space>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditing(row);
                            setOpen(true);
                        }}
                    >
                        编辑
                    </Button>
                    {!row.enabled ? (
                        <Button
                            type="link"
                            size="small"
                            onClick={() =>
                                toggleHrTemplate(appId, row.id, true).then(load).catch(() => {})
                            }
                        >
                            启用
                        </Button>
                    ) : (
                        <Button
                            type="link"
                            size="small"
                            onClick={() =>
                                toggleHrTemplate(appId, row.id, false).then(load).catch(() => {})
                            }
                        >
                            停用
                        </Button>
                    )}
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                            deleteHrTemplate(appId, row.id).then(load);
                        }}
                    >
                        删除
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <AppContent>
            <Card
                title="模板管理"
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditing(null);
                            setOpen(true);
                        }}
                    >
                        新建模板
                    </Button>
                }
            >
                <Form form={form} layout="inline" onFinish={load} style={{marginBottom: 16}}>
                    <Form.Item name="taskType">
                        <Select
                            placeholder="通知类别"
                            allowClear
                            style={{width: 180}}
                            options={TYPE_OPTIONS}
                        />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit">
                            搜索
                        </Button>
                        <Button
                            onClick={() => {
                                form.resetFields();
                                load();
                            }}
                        >
                            重置
                        </Button>
                    </Space>
                </Form>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    pagination={{pageSize: 10, showTotal: total => `共 ${total} 条记录`}}
                />
                <TemplateFormModal
                    open={open}
                    appId={appId}
                    editing={editing}
                    onClose={() => setOpen(false)}
                    onSaved={() => {
                        setOpen(false);
                        load();
                    }}
                />
            </Card>
        </AppContent>
    );
}
